import React, { memo, useState, useEffect, useRef } from 'react';
import { useGraphInit } from './hooks/useGraphInit';
import { useGraphData } from './hooks/useGraphData';
import { useNodeInteractions } from './hooks/useNodeInteractions';
import { useSimulation } from './hooks/useSimulation';
import { useEdgeDialog } from './hooks/useEdgeDialog';
import { useSubGraph } from './hooks/useSubGraph';
import FloatingToolbar from './components/FloatingToolbar';
import StatusBar from './components/StatusBar';
import SubGraphPanel from './components/SubGraphPanel';
import EdgeTypeDialog from './components/EdgeTypeDialog';
import NodeMenuTrigger from './components/NodeMenuTrigger';
import { applyParallelEdgeOffset } from './utils/edgeOffset';
import { mergeSameTypeEdges } from './utils/mergeSameTypeEdges';
import { applyInvocationZigzag } from './utils/invocationZigzag';
import './styles/index.css';
import type { OPMGraphProps } from './types';

const OPMEditorX6Svg: React.FC<OPMGraphProps> = memo(({
  initialNodes,
  initialEdges,
  onStatusChange,
  onNodeSelect,
  onEdgeCreate,
}) => {
  const [statusText, setStatusText] = useState('就绪');
  const [selectedNode, setSelectedNode] = useState<any>(null);
  const initializedRef = useRef(false);

  // 1. 图初始化
  const { containerRef, graphRef, graph } = useGraphInit();

  // 2. 数据加载
  const { loadNodes } = useGraphData(graphRef);

  // 3. 子图管理
  const subGraph = useSubGraph(graphRef);

  // 4. 边对话框（需要提前定义，因为 useNodeInteractions 需要 showEdgeDialog）
  const edgeDialog = useEdgeDialog(graphRef, { setStatusText, onStatusChange });

  // 5. 节点交互
  useNodeInteractions(graphRef, graph, {
    setStatusText,
    showEdgeDialog: edgeDialog.showEdgeDialog,
    onNodeSelect: (node) => {
      setSelectedNode(node ?? null);
      onNodeSelect?.(node);
    },
  });

  // 6. 仿真控制
  const simulation = useSimulation(graphRef, setStatusText);

  // 7. 初始数据加载 + 子图初始化
  useEffect(() => {
    console.log('[OPMEditorX6] Data loading effect triggered:', {
      hasGraph: !!graph,
      hasInitialNodes: !!initialNodes,
      nodeCount: initialNodes?.length,
      alreadyInitialized: initializedRef.current,
    });

    if (graph && initialNodes && !initializedRef.current) {
      initializedRef.current = true;
      console.log('[OPMEditorX6] Loading nodes...');
      // 加载节点到 X6 画布
      loadNodes(initialNodes);

      // 收集已加载的节点 ID
      const nodeIds = new Set(initialNodes.map((n: any) => n.id));
      console.log('[OPMEditorX6] Nodes loaded, IDs:', Array.from(nodeIds));

      // 过滤有效的边
      const validEdges = (initialEdges || []).filter((edgeData: any) => {
        const sourceId = typeof edgeData.source === 'string'
          ? edgeData.source
          : edgeData.source?.cell;
        const targetId = typeof edgeData.target === 'string'
          ? edgeData.target
          : edgeData.target?.cell;
        return nodeIds.has(sourceId) && nodeIds.has(targetId);
      });
      console.log('[OPMEditorX6] Valid edges:', validEdges.length);

      // 延迟添加边，确保节点布局完成
      setTimeout(() => {
        const g = graphRef.current;
        if (g) {
          console.log('[OPMEditorX6] Adding edges...');
          validEdges.forEach((edgeData: any) => {
            g.addEdge(edgeData);
          });

          // 对平行边施加偏移，避免同一对节点间的多条边重叠
          applyParallelEdgeOffset(g);

          // 合并同类型边：多条同类型边指向同一目标时合并展示
          mergeSameTypeEdges(g);

          // 调用关系折线样式
          applyInvocationZigzag(g);

          // 所有节点和边加载完成后，调整视图
          setTimeout(() => {
            console.log('[OPMEditorX6] Calling zoomToFit...');
            g.zoomToFit({ padding: 60, maxScale: 1.2 });

            console.log('[OPMEditorX6] Initialization complete');
          }, 100);
        }
      }, 50);

      // 初始化子图根节点
      subGraph.initializeRootGraph(initialNodes, validEdges);
    }
  }, [graph, initialNodes, initialEdges, loadNodes]);

  // 8. 将子图上下文暴露到全局（供 NodeContextMenu 使用）
  useEffect(() => {
    (window as any).__OPM_SUBGRAPH__ = {
      createSubGraph: subGraph.createSubGraph,
      navigateToGraph: subGraph.navigateToGraph,
      currentGraphId: subGraph.currentGraphId,
      graphPath: subGraph.graphPath,
    };
    return () => {
      (window as any).__OPM_SUBGRAPH__ = null;
    };
  }, [subGraph.createSubGraph, subGraph.navigateToGraph, subGraph.currentGraphId, subGraph.graphPath]);

  // 9. 状态通知
  useEffect(() => {
    onStatusChange?.(statusText);
  }, [statusText, onStatusChange]);

  return (
    <div style={{ display: 'flex', width: '100%', height: '100%', transform: 'none', WebkitTransform: 'none' }}>
      {/* 左侧：子图层级面板 */}
      <SubGraphPanel
        tree={subGraph.subGraphTree}
        currentGraphId={subGraph.currentGraphId}
        onNavigate={subGraph.navigateToGraph}
      />

      {/* 右侧：图编辑器 */}
      <div className="opm-x6-container" style={{ flex: 1, position: 'relative', transform: 'none', WebkitTransform: 'none' }}>
        <div ref={containerRef} style={{ width: '100%', height: '100%', transform: 'none', WebkitTransform: 'none' }} />
        <FloatingToolbar
          graph={graph}
          simulationRunning={simulation.simulationRunning}
          dataFlowActive={simulation.dataFlowActive}
          onStartSimulation={simulation.startSimulation}
          onStopSimulation={simulation.stopSimulation}
          onStartDataFlow={simulation.startDataFlow}
          onStopDataFlow={simulation.stopDataFlow}
        />
        <StatusBar
          statusText={statusText}
          simulationRunning={simulation.simulationRunning}
        />
        <EdgeTypeDialog
          visible={edgeDialog.dialogVisible}
          sourceLabel={edgeDialog.dialogEdgeInfo.sourceLabel}
          targetLabel={edgeDialog.dialogEdgeInfo.targetLabel}
          sourceNodeType={edgeDialog.dialogEdgeInfo.sourceNodeType}
          isProcessSubGraph={
            subGraph.currentGraphId !== 'root' &&
            subGraph.graphPath?.[subGraph.graphPath.length - 1]?.nodeType === 'process'
          }
          onConfirm={edgeDialog.handleEdgeTypeConfirm}
          onCancel={edgeDialog.handleEdgeTypeCancel}
        />
        {selectedNode && graph && subGraph.currentGraphId === 'root' && (
          <NodeMenuTrigger node={selectedNode} graph={graph} />
        )}
      </div>
    </div>
  );
});

OPMEditorX6Svg.displayName = 'OPMEditorX6Svg';

export default OPMEditorX6Svg;
export { OPMEditorX6Svg };
export type { OPMGraphProps } from './types';